import { useEffect, useMemo, useRef, useState } from 'react';
import { FUEL_TYPE_DATA } from '../../../../../../constants/carline-constants';
import { FIELD_NAME, FORM_SUBMIT_STATUS } from '../../../../../../constants/form-constants';
import { useApp } from '../../../../../../contexts/app-context';
import { formService } from '../../../../../../form-service';
import { getCarlines } from '../../../../../../service/api/get-carlines';
import { getExternalContentSalesgroups } from '../../../../../../service/api/get-salesgroups';
import { externalContentManager } from '../../../../../../service/external-content';
import { Carline, SalesgroupWithCarline } from '../../../../../../types/vehicle-types';

export type MotorizacionFilterKey = 'combustion' | 'hibrido_enchufable' | 'electrico';

const MOTORIZACION_FILTERS: { key: MotorizacionFilterKey; label: string; combustibles: string[] }[] = [
	{
		key: 'combustion',
		label: 'Combustión',
		combustibles: [FUEL_TYPE_DATA.GASOLINA, FUEL_TYPE_DATA.DIESEL, FUEL_TYPE_DATA.HIBRIDO_ECO],
	},
	{
		key: 'hibrido_enchufable',
		label: 'Híbridos enchufables',
		combustibles: [FUEL_TYPE_DATA.HIBRIDO, FUEL_TYPE_DATA.HIBRIDO_ENCHUFABLE],
	},
	{
		key: 'electrico',
		label: 'Eléctricos',
		combustibles: [FUEL_TYPE_DATA.ELECTRICO],
	},
];

interface Props {
	carlines?: Carline[];
	salesgroups?: SalesgroupWithCarline[];
}

export const useVehicleFilter = (props: Props) => {
	const { carlines, salesgroups } = props;
	const { setAppError, setAppErrorLog } = useApp();
	const [allOptions, setAllOptions] = useState<Carline[] | SalesgroupWithCarline[]>([]);
	const [activeFilter, setActiveFilter] = useState<MotorizacionFilterKey | null>(null);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const [hasError, setHasError] = useState(false);
	const fieldNameRef = useRef<FIELD_NAME>(FIELD_NAME.SALESGROUP);
	const [isCarlineView, setIsCarlineView] = useState<boolean>(false);

	useEffect(() => {
		formService.setFieldRequired(FIELD_NAME.DEALER, true);

		// Priorizamos la carga de Salesgroups
		if (salesgroups) {
			fieldNameRef.current = FIELD_NAME.SALESGROUP;
			mapppingSalesgroupOptions(salesgroups);
		}
		if (carlines) {
			fieldNameRef.current = FIELD_NAME.CARLINE;
			mapppingCarlineOptions(carlines);
		} else {
			getExternalContentSalesgroups().then((salesgroups) => {
				if (salesgroups) {
					fieldNameRef.current = FIELD_NAME.SALESGROUP;
					mapppingSalesgroupOptions(salesgroups);
				} else {
					getCarlines().then((carlines) => {
						if (!carlines) {
							setAppError(true);
							setAppErrorLog('No se ha obtenido ningún carline.');
						} else if (carlines) {
							fieldNameRef.current = FIELD_NAME.CARLINE;
							mapppingCarlineOptions(carlines);
						}
					});
				}
			});
		}
	}, []);

	useEffect(() => {
		const fieldName = fieldNameRef.current;
		formService.setFieldRequired(fieldName, true);

		const unsubscribeField = formService.subscribeToField(fieldName, {
			onChange: (value) => {
				if (value) setHasError(false);
			},
			onError: (error) => setHasError(error),
		});
		const unsubscribeStatus = formService.subscribeToFormSubmitStatus((status) => {
			if (status === FORM_SUBMIT_STATUS.INVALID) {
				const value = formService.getFieldValue(fieldName);
				setHasError(!value);
			}
		});
		return () => {
			unsubscribeField();
			unsubscribeStatus();
		};
	}, []);

	const mapppingCarlineOptions = (carlines: Carline[]) => {
		// ordenar por el orden de los carlines en la URL (si hay)
		const urlCarlines = externalContentManager.carlines;
		if (urlCarlines) {
			const orderedOptions = [...carlines].sort(
				(a, b) => getUrlOrderIndex(a, urlCarlines) - getUrlOrderIndex(b, urlCarlines),
			);
			setAllOptions(orderedOptions);
		} else {
			// ordenar por prioridad y luego por carline title
			const orderedOptions = [...carlines].sort((a, b) => {
				if (a.prioridad === b.prioridad) {
					return a.title.localeCompare(b.title);
				}
				return b.prioridad - a.prioridad;
			});

			setAllOptions(orderedOptions);
		}
	};

	const getSalesgroupUrlOrderIndex = (salesgroup: SalesgroupWithCarline, urlOrder: string[]): number => {
		const index = urlOrder.findIndex(
			(urlSalesgroup) => urlSalesgroup === salesgroup.name || urlSalesgroup === String(salesgroup.code),
		);

		return index === -1 ? urlOrder.length : index;
	};

	const mapppingSalesgroupOptions = (salesgroups: SalesgroupWithCarline[]) => {
		const urlSalesgroups = externalContentManager.salesgroups;
		const urlCarlines = externalContentManager.carlines;

		// ordenar por el orden de los salesgroups en la URL (si hay)
		if (urlSalesgroups) {
			const orderedOptions = [...salesgroups].sort(
				(a, b) => getSalesgroupUrlOrderIndex(a, urlSalesgroups) - getSalesgroupUrlOrderIndex(b, urlSalesgroups),
			);
			setAllOptions(orderedOptions);
		}
		// ordenar por el orden de los carlines en la URL (si hay)
		else if (urlCarlines) {
			const orderedOptions = [...salesgroups].sort(
				(a, b) => getUrlOrderIndex(a.carline, urlCarlines) - getUrlOrderIndex(b.carline, urlCarlines),
			);
			setAllOptions(orderedOptions);
		}
		// ordenar por prioridad del carline y luego por carline title (por defecto)
		else {
			const orderedOptions = [...salesgroups].sort((a, b) => {
				if (a.carline.prioridad === b.carline.prioridad) {
					return a.carline.title.localeCompare(b.carline.title);
				}
				return b.carline.prioridad - a.carline.prioridad;
			});
			setAllOptions(orderedOptions);
		}
	};

	const getUrlOrderIndex = (carline: Carline, urlOrder: string[]): number => {
		const index = urlOrder.findIndex(
			(urlCarline) => urlCarline === carline.name || urlCarline === String(carline.code),
		);

		return index === -1 ? urlOrder.length : index;
	};

	const hasFilter = (option: Carline | SalesgroupWithCarline, filterKey: MotorizacionFilterKey): boolean => {
		const filter = MOTORIZACION_FILTERS.find((f) => f.key === filterKey);
		if (!filter) return false;

		const carline = 'carline' in option ? option.carline : option;
		const combustibles = carline.combustibles ?? [];

		return combustibles.some((combustible) => filter.combustibles.includes(combustible));
	};

	const motorizacionFilters = useMemo(
		() => MOTORIZACION_FILTERS.filter((filter) => allOptions.some((option) => hasFilter(option, filter.key))),
		[allOptions],
	);

	const options = useMemo((): Carline[] | SalesgroupWithCarline[] => {
		if (!activeFilter) return allOptions;
		return allOptions.filter((option) => hasFilter(option, activeFilter)) as Carline[] | SalesgroupWithCarline[];
	}, [allOptions, activeFilter]);

	const changeView = () => {
		setIsCarlineView((prev) => !prev);
	};

	const handleFilterClick = (filterKey: MotorizacionFilterKey | null) => {
		setActiveFilter(filterKey);

		//Para evitar enviar si se ha seleccionado previamente y cambiado de filtro
		setActiveIndex(null);
		formService.setFieldValue(fieldNameRef.current, undefined);
	};

	const handleVehicleSelection = (e: React.MouseEvent, option: Carline | SalesgroupWithCarline, index: number) => {
		e.stopPropagation();
		setActiveIndex(index);
		const fieldName = 'carline' in option ? FIELD_NAME.SALESGROUP : FIELD_NAME.CARLINE;
		formService.setFieldValue(fieldName, option);
		formService.setFieldError(fieldName, false);

		// then change view to VehicleCardSelected (transition)
		changeView();
	};

	return {
		activeFilter,
		activeIndex,
		hasError,
		motorizacionFilters,
		options,
		isCarlineView,
		changeView,
		handleFilterClick,
		handleVehicleSelection,
	};
};
